/**
 * TikTok US 店铺表现（SPS）—— 接口层
 *
 * 契约：《SPS 店铺表现 —— 前端接入指南》(sps-web-api-and-frontend-guide.md，2026-10-10 版)
 * 网关：storeHomeNVClient 的 baseURL 已是 `/overseaim-store-home`，因此 path 从 `/api_gateway` 开始。
 *
 * 两条取数路线（理解这个才不会把正常现象当 bug）：
 *   - 查表接口：overview / problem-orders / aftersales 及其导出 —— 定时任务落库的**上一轮快照**，可多店
 *   - 实时接口：shop/overview / shop/metrics / shop/diagnosis / metric/top-items —— 当次直连官方，**仅单店**，有 Redis 短 TTL 缓存（默认 1h）
 */
import { ImResponse, resolve, storeHomeNVClient } from 'services/api';

const PREFIX = '/api_gateway/dashboard/sps';

/**
 * GET 列表接口的数组入参后端要求「逗号分隔」（见接入指南 3.1 / 3.6 / 3.9），
 * 项目里没有全局 paramsSerializer，这里统一 join。
 */
function joinIds(list?: Array<string | number>) {
  return list?.length ? list.join(',') : undefined;
}

/** RangeFilter → query params（数组 join，空数组按「不限」处理） */
function toRangeParams(filter: Dashboard.TikTokUS.RangeFilter) {
  return {
    platform: filter.platform,
    departmentIds: joinIds(filter.departmentIds),
    storeType: filter.storeType,
    storeIds: joinIds(filter.storeIds),
  };
}

/* ==================== ① 排行榜 ==================== */

/**
 * GET /sps/overview
 * 排行榜首屏（分页）。**不接受时间范围**，展示最新一轮快照。
 * 排序由服务端全局完成，前端不要再对当前页本地排序；spsScore 为 null 的店铺恒在最后。
 */
export function getSpsOverview(params: Dashboard.TikTokUS.OverviewQuery) {
  return resolve(
    storeHomeNVClient.get<
      ImResponse<Dashboard.TikTokUS.PageInfo<Dashboard.TikTokUS.ShopOverviewRow>>
    >(`${PREFIX}/overview`, {
      params: {
        ...toRangeParams(params),
        pageNum: params.pageNum,
        pageSize: params.pageSize,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
      },
    }),
  );
}

/**
 * POST /sps/overview/export
 * 导出排行榜（异步）。只确认任务已受理，不返回文件；完成后由导出中心推送下载。
 * ⚠️ 导出恒为默认排序（近 7 天订单量降序），**不跟随页面的 sortBy**。
 */
export function exportSpsOverview(body: Dashboard.TikTokUS.ExportOverviewBody) {
  return resolve(storeHomeNVClient.post<ImResponse<null>>(`${PREFIX}/overview/export`, body));
}

/* ==================== ② 详情页（实时接口，仅单店） ==================== */

/**
 * GET /sps/shop/overview
 * 详情抽屉顶部总览。抽屉打开时与 ④ 一起发（只这两个），其余等用户点指标卡再发。
 */
export function getSpsShopOverview(params: Dashboard.TikTokUS.ShopOverviewQuery) {
  return resolve(
    storeHomeNVClient.get<ImResponse<Dashboard.TikTokUS.ShopOverviewResp>>(
      `${PREFIX}/shop/overview`,
      { params },
    ),
  );
}

/**
 * GET /sps/shop/metrics
 * 6 个指标**一次返回**（官方批量接口），**不要循环调 6 次**。
 */
export function getSpsShopMetrics(params: Dashboard.TikTokUS.ShopMetricsQuery) {
  return resolve(
    storeHomeNVClient.get<ImResponse<Dashboard.TikTokUS.ShopMetricDetail[]>>(
      `${PREFIX}/shop/metrics`,
      { params },
    ),
  );
}

/**
 * GET /sps/shop/diagnosis
 * 单指标诊断（点开指标卡时拉）。
 */
export function getSpsShopDiagnosis(params: Dashboard.TikTokUS.DiagnosisQuery) {
  return resolve(
    storeHomeNVClient.get<ImResponse<Dashboard.TikTokUS.ShopMetricDiagnosis>>(
      `${PREFIX}/shop/diagnosis`,
      { params },
    ),
  );
}

/**
 * GET /sps/metric/top-items
 * 问题商品下钻（点开指标卡时拉，与 diagnosis 并发）。
 * ⚠️ 四个实时接口里最贵的一个，且常被连点 —— 调用方需做防抖。
 */
export function getSpsMetricTopItems(params: Dashboard.TikTokUS.TopItemsQuery) {
  return resolve(
    storeHomeNVClient.get<ImResponse<Dashboard.TikTokUS.TopItemsResp>>(
      `${PREFIX}/metric/top-items`,
      { params },
    ),
  );
}

/* ==================== ③ 问题订单明细 ==================== */

/**
 * GET /sps/problem-orders
 * ⚠️ metricCode **单值必填**、startDate/endDate 必填（order_create_time 是唯一能走索引的列）。
 * 想看多个指标就分别查几次。
 */
export function getSpsProblemOrders(params: Dashboard.TikTokUS.ProblemOrdersQuery) {
  return resolve(
    storeHomeNVClient.get<
      ImResponse<Dashboard.TikTokUS.PageInfo<Dashboard.TikTokUS.ProblemOrderRow>>
    >(`${PREFIX}/problem-orders`, {
      params: {
        ...toRangeParams(params),
        metricCode: params.metricCode,
        startDate: params.startDate,
        endDate: params.endDate,
        pageNum: params.pageNum,
        pageSize: params.pageSize,
      },
    }),
  );
}

/**
 * POST /sps/problem-orders/export
 * 一个指标一个 sheet；本期恒传当前选中的单个指标（单 sheet）。
 * 单次上限 100000 行，超出由后端**拒绝**（不截断）。
 */
export function exportSpsProblemOrders(body: Dashboard.TikTokUS.ExportProblemOrdersBody) {
  return resolve(storeHomeNVClient.post<ImResponse<null>>(`${PREFIX}/problem-orders/export`, body));
}

/* ==================== ④ 售后处理明细 ==================== */

/**
 * GET /sps/aftersales
 * AHT 专用明细。⚠️ 不认 metricCode / metricCodes（传了被忽略、不报错）。
 * 本期**不提供**售后类型筛选，aftersalesTypes 一律不传。
 */
export function getSpsAftersales(params: Dashboard.TikTokUS.AftersalesQuery) {
  return resolve(
    storeHomeNVClient.get<
      ImResponse<Dashboard.TikTokUS.PageInfo<Dashboard.TikTokUS.AftersalesRow>>
    >(`${PREFIX}/aftersales`, {
      params: {
        ...toRangeParams(params),
        aftersalesTypes: joinIds(params.aftersalesTypes),
        startDate: params.startDate,
        endDate: params.endDate,
        pageNum: params.pageNum,
        pageSize: params.pageSize,
      },
    }),
  );
}

/**
 * POST /sps/aftersales/export
 * 单 sheet「售后处理明细」。单次上限 100000 行，超出被拒绝。
 */
export function exportSpsAftersales(body: Dashboard.TikTokUS.ExportAftersalesBody) {
  return resolve(storeHomeNVClient.post<ImResponse<null>>(`${PREFIX}/aftersales/export`, body));
}
